import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import { useConfig } from "../../context/ConfigContext"
import API from "../../api/axios"
import "../enrollment/enroll.css"
import "./live.css"

const platformIcons = { zoom: "fa-video", google_meet: "fa-google", microsoft_teams: "fa-microsoft", other: "fa-link" }

const BookingModal = ({ session, isOpen, onClose, onBooked }) => {
  const { user }           = useAuth()
  const { paymentEnabled } = useConfig()
  const navigate           = useNavigate()
  const isFree             = session?.isFree
  const showComingSoon     = !isFree && !paymentEnabled

  const [step,    setStep]    = useState(1)
  const [method,  setMethod]  = useState("mpesa")
  const [phone,   setPhone]   = useState(user?.phone || "")
  const [loading, setLoading] = useState(false)
  const [errMsg,  setErrMsg]  = useState("")
  const [payData, setPayData] = useState(null)

  if (!isOpen || !session) return null

  const reset = () => { setStep(1); setLoading(false); setErrMsg(""); setPayData(null) }
  const handleClose = () => { reset(); onClose() }

  const formatDate = (d) => new Date(d).toLocaleString("en-KE", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  })

  // Free booking
  const handleFreeBook = async () => {
    setLoading(true); setErrMsg("")
    try {
      await API.post(`/live-sessions/${session.id}/book`)
      setStep(3)
      if (onBooked) onBooked()
    } catch (err) {
      const msg = err.response?.data?.message || "Booking failed."
      if (msg.includes("already booked")) { setStep(3); if (onBooked) onBooked() }
      else { setErrMsg(msg); setStep(4) }
    } finally { setLoading(false) }
  }

  // Paid booking — initiate payment
  const handlePayment = async (e) => {
    e.preventDefault()
    if (method === "mpesa" && !phone.trim()) return setErrMsg("Please enter your M-Pesa phone number.")
    setLoading(true); setErrMsg("")
    try {
      const { data } = await API.post(`/live-sessions/${session.id}/pay`, { method, phone: phone.trim() })
      setPayData(data); setStep(3)
    } catch (err) {
      const msg = err.response?.data?.message || "Payment failed."
      if (msg.includes("Already booked")) { setStep(3); if (onBooked) onBooked() }
      else { setErrMsg(msg); setStep(4) }
    } finally { setLoading(false) }
  }

  return (
    <>
      <div className="enroll-backdrop" onClick={handleClose} />
      <div className="enroll-modal" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="enroll-modal-header">
          <div className="enroll-course-info">
            <div className="enroll-course-icon">
              <i className={`fa ${platformIcons[session.platform] || "fa-video"}`}></i>
            </div>
            <div>
              <h3>{session.title}</h3>
              <span>{formatDate(session.sessionDate)}</span>
            </div>
          </div>
          <button className="enroll-close-btn" onClick={handleClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        {/* Step 1 — Confirm */}
        {step === 1 && (
          <div className="enroll-body">
            <div className="booking-step-label"><i className="fa fa-calendar-check"></i> Confirm Booking</div>

            <div className="booking-session-meta">
              <div className="meta-row"><i className="fa fa-heading"></i><span><strong>{session.title}</strong></span></div>
              <div className="meta-row"><i className="fa fa-calendar"></i><span>{formatDate(session.sessionDate)}</span></div>
              <div className="meta-row"><i className="fa fa-clock"></i><span>{session.duration} minutes</span></div>
              <div className="meta-row"><i className="fa fa-user-tie"></i><span>{session.instructor || "Mr. Ali"}</span></div>
              <div className="meta-row"><i className="fa fa-users"></i><span>{session.maxParticipants} max participants</span></div>
              <div className="meta-row"><i className="fa fa-user"></i><span><strong>{user?.name}</strong> · {user?.email}</span></div>
            </div>

            <div className="enroll-summary">
              <div className="enroll-summary-row total">
                <span>Price</span>
                <strong>{isFree ? <span className="price-free-tag">FREE</span> : `KES ${Number(session.price).toLocaleString()}`}</strong>
              </div>
            </div>

            {isFree ? (
              <button className="enroll-btn primary" onClick={handleFreeBook} disabled={loading}>
                {loading ? <><i className="fa fa-spinner fa-spin"></i> Booking…</> : <><i className="fa fa-calendar-check"></i> Book Free Spot</>}
              </button>
            ) : showComingSoon ? (
              <div className="enroll-coming-soon">
                <div className="coming-soon-icon"><i className="fa fa-clock"></i></div>
                <h4>Payments Coming Soon</h4>
                <p>Online payments are not yet active. Please contact us to book this session.</p>
                <div className="coming-soon-actions">
                  <a href="https://wa.me/254723000200" target="_blank" rel="noreferrer"
                    className="enroll-btn primary" style={{ textDecoration:"none", justifyContent:"center" }}>
                    <i className="fab fa-whatsapp"></i> WhatsApp Us
                  </a>
                  <a href="mailto:info@rovers.ke"
                    className="enroll-btn ghost" style={{ textDecoration:"none", justifyContent:"center" }}>
                    <i className="fa fa-envelope"></i> Email Us
                  </a>
                </div>
              </div>
            ) : (
              <button className="enroll-btn primary" onClick={() => setStep(2)}>
                <i className="fa fa-lock-open"></i> Proceed to Payment
              </button>
            )}
            <button className="enroll-btn ghost" onClick={handleClose}>Cancel</button>
          </div>
        )}

        {/* Step 2 — Payment */}
        {step === 2 && (
          <div className="enroll-body">
            <div className="booking-step-label"><i className="fa fa-credit-card"></i> Payment</div>
            <div className="enroll-amount-banner">
              <span>Total Due</span>
              <strong>KES {Number(session.price).toLocaleString()}</strong>
            </div>
            {errMsg && <div className="enroll-error"><i className="fa fa-exclamation-circle"></i> {errMsg}</div>}
            <form onSubmit={handlePayment}>
              <div className="enroll-field">
                <label>Payment Method</label>
                <div className="enroll-methods">
                  {[
                    { id: "mpesa",  icon: "fa fa-mobile-alt", label: "M-Pesa" },
                    { id: "bank",   icon: "fa fa-university",  label: "Bank Transfer" },
                    { id: "manual", icon: "fa fa-hand-holding-usd", label: "Cash / Manual" },
                  ].map(m => (
                    <label key={m.id} className={`enroll-method ${method === m.id ? "selected" : ""}`}>
                      <input type="radio" name="method" value={m.id} checked={method === m.id} onChange={() => setMethod(m.id)} />
                      <i className={m.icon}></i>{m.label}
                    </label>
                  ))}
                </div>
              </div>
              {method === "mpesa" && (
                <div className="enroll-field">
                  <label>M-Pesa Phone *</label>
                  <input type="tel" placeholder="0722 000 200" value={phone} onChange={e => setPhone(e.target.value)} required />
                  <small>Number you'll send M-Pesa from</small>
                </div>
              )}
              {(method === "bank" || method === "manual") && (
                <div className="enroll-info-box">
                  <i className="fa fa-info-circle"></i>
                  <div><strong>Instructions</strong><p>After submitting, our team will share payment details. Your booking is activated once payment is confirmed.</p></div>
                </div>
              )}
              <div className="enroll-user-recap">
                <div className="enroll-recap-row"><i className="fa fa-user"></i><span>{user?.name}</span></div>
                <div className="enroll-recap-row"><i className="fa fa-envelope"></i><span>{user?.email}</span></div>
              </div>
              <div className="enroll-actions">
                <button type="button" className="enroll-btn ghost" onClick={() => setStep(1)}><i className="fa fa-arrow-left"></i> Back</button>
                <button type="submit" className="enroll-btn primary" disabled={loading}>
                  {loading ? <><i className="fa fa-spinner fa-spin"></i> Submitting…</> : <><i className="fa fa-paper-plane"></i> Submit & Confirm</>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3 — Success */}
        {step === 3 && (
          <div className="enroll-body enroll-success">
            <div className="enroll-success-icon"><i className={isFree ? "fa fa-calendar-check" : "fa fa-check-circle"}></i></div>
            <h3>{isFree ? "Spot Reserved!" : "Request Submitted!"}</h3>
            {isFree ? (
              <p>You're booked for <strong>{session.title}</strong> on {formatDate(session.sessionDate)}.<br />Meeting details will appear in your dashboard.</p>
            ) : (
              <>
                <p>Your payment request has been received. Your booking will be confirmed once payment is verified.</p>
                {payData?.instructions && (
                  <div className="enroll-instructions" style={{ marginBottom: 16 }}>
                    <i className="fa fa-info-circle"></i>{payData.instructions}
                  </div>
                )}
                <p className="enroll-pending-note"><i className="fa fa-clock"></i>You'll receive an email once confirmed.</p>
              </>
            )}
            <button className="enroll-btn primary" onClick={() => { handleClose(); navigate("/dashboard") }}>
              <i className="fa fa-th-large"></i> Go to Dashboard
            </button>
            <button className="enroll-btn ghost" onClick={handleClose}>Back to Sessions</button>
          </div>
        )}

        {/* Step 4 — Error */}
        {step === 4 && (
          <div className="enroll-body enroll-error-state">
            <div className="enroll-error-icon"><i className="fa fa-exclamation-triangle"></i></div>
            <h3>Something went wrong</h3>
            <p>{errMsg}</p>
            <button className="enroll-btn primary" onClick={() => { setStep(isFree ? 1 : 2); setErrMsg("") }}>Try Again</button>
            <button className="enroll-btn ghost" onClick={handleClose}>Cancel</button>
          </div>
        )}

      </div>
    </>
  )
}

export default BookingModal
